import { useReducer } from 'react';

export type RequestState<T> = {
  isLoading: boolean,
  data: T | null,
  error: Error | unknown | null
}

const defaultInitialState: RequestState<any> = {
  isLoading: false,
  data: null,
  error: null,
};

export const RequestActions = {
  REQUEST: 'REQUEST',
  REQUEST_PRESERVE_DATA: 'REQUEST_PRESERVE_DATA',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  RESET: 'RESET',
} as const;

type RequestActionsType = typeof RequestActions[keyof typeof RequestActions];

type RequestAction<T> = {
  type: RequestActionsType,
  payload?: RequestState<T>['data'] | RequestState<T>['error']
}

type RequestReducer<T> = (state: RequestState<T>, action: RequestAction<T>) => RequestState<T>

function reducer<T>(state: RequestState<T>, action: RequestAction<T>): RequestState<T> {
  if (!action.type) {
    return state;
  }

  switch (action.type) {
    case RequestActions.REQUEST: {
      return {
        ...defaultInitialState,
        isLoading: true,
      };
    }
    case RequestActions.REQUEST_PRESERVE_DATA: {
      return {
        ...state,
        error: null,
        isLoading: true,
      };
    }
    case RequestActions.ERROR: {
      return {
        ...defaultInitialState,
        error: action.payload as RequestState<T>['error'],
      };
    }
    case RequestActions.SUCCESS: {
      return {
        ...defaultInitialState,
        data: action.payload as RequestState<T>['data'],
      };
    }
    case RequestActions.RESET: {
      return defaultInitialState;
    }
    default:
      return state;
  }
};

export type RequestReducerOptions<T> = {
  initialState?: Partial<RequestState<T>>
}

export function useRequestReducer<T>(initialState: Partial<RequestState<T>> = {}) {
  return useReducer<RequestReducer<T>>(reducer, { ...defaultInitialState, ...initialState });
};
